import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Reveal from "@/components/base/Reveal";
import ContactForm from "@/pages/about/components/ContactForm";
import useSeo from "@/hooks/useSeo";
import { principles, siteInfo, testimonials } from "@/mocks/site";

const steps = [
  {
    step: "01",
    title: "话题进入视野",
    desc: "当某个关键词在短时间内被大量检索或讨论时，它会被加入待整理清单。",
  },
  {
    step: "02",
    title: "核对公开信息",
    desc: "只采用公开可查的报道、平台内容与当事人账号发布的信息，交叉比对后再落笔。",
  },
  {
    step: "03",
    title: "按时间线成稿",
    desc: "把关键节点按日期排列，区分「当事人回应」「媒体转述」「网友讨论」三类来源。",
  },
  {
    step: "04",
    title: "持续更新标注",
    desc: "出现新的公开回应时补充内容并更新日期，旧版本信息保留在正文中，方便对照。",
  },
];

export default function AboutPage() {
  const origin = window.location.origin;

  useSeo({
    title: "关于我们 - 15黑料网的定位与内容原则",
    description:
      "了解15黑料网：一个以黑料关键词搜索与黑料索引为核心的检索工具站。我们只整理公开可见的话题线索，不收录个人隐私，不替事件下结论，并提供内容纠错与线索补充渠道。",
    keywords: "关于15黑料网,黑料索引工具,内容原则,词典整理,联系方式",
    path: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于我们",
        url: `${origin}/about`,
        inLanguage: "zh-CN",
        description: "15黑料网的品牌介绍、网站定位、内容原则与联系方式。",
        isPartOf: { "@type": "WebSite", name: siteInfo.name, url: origin },
        mainEntity: {
          "@type": "Organization",
          name: siteInfo.name,
          email: siteInfo.email,
          foundingDate: siteInfo.since,
          description: siteInfo.intro,
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: origin },
          { "@type": "ListItem", position: 2, name: "关于我们", item: `${origin}/about` },
        ],
      },
    ],
  });

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="relative overflow-hidden border-b border-background-200/70">
          <div className="absolute inset-0 paper-grain" />
          <div className="relative mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-14">
            <Breadcrumbs items={[{ label: "首页", path: "/" }, { label: "关于我们" }]} />
            <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              关于 {siteInfo.name}
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {siteInfo.intro}
            </p>
            <div className="mt-8 flex flex-wrap gap-x-10 gap-y-4">
              {siteInfo.stats.map((stat) => (
                <div key={stat.label} className="flex items-baseline gap-2">
                  <span className="font-heading text-2xl font-bold text-primary-600">{stat.value}</span>
                  <span className="text-xs text-foreground-500">
                    {stat.label}
                    {stat.suffix}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16" aria-labelledby="about-position-title">
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-14">
            <div>
              <p className="text-xs font-medium tracking-[0.25em] text-primary-600">POSITIONING</p>
              <h2 id="about-position-title" className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                网站定位
              </h2>
              <div className="mt-5 space-y-4 text-sm leading-[1.9] text-foreground-700">
                <p>
                  我们把自己定位成「索引」而不是「媒体」。索引的工作是把分散的信息归位，让你在需要的时候能查到，
                  而不是替你去判断谁对谁错。
                </p>
                <p>
                  因此站内的每个词条都尽量保留时间顺序和来源类别。你可以看到一条线索是什么时候出现的、
                  当事人有没有回应、回应发生在什么时候，而不是只拿到一个没有上下文的结论。
                </p>
                <p>
                  网站自 {siteInfo.since} 年起持续整理，目前覆盖娱乐人物、影视综艺、音乐现场、网红主播、
                  商业风云与体育竞技六个方向。
                </p>
              </div>
            </div>
            <div className="rounded-lg bg-foreground-950 p-6 md:p-8">
              <p className="text-xs font-medium tracking-[0.25em] text-accent-400">PRINCIPLE</p>
              <h2 className="mt-2 font-heading text-xl font-bold text-background-50 md:text-2xl">发展理念</h2>
              <p className="mt-4 text-sm leading-relaxed text-background-300">
                信息可以热闹，但整理必须冷静。我们相信一个能长久用下去的检索工具，
                靠的不是更刺激的标题，而是更清楚的脉络和更稳定的口径。
              </p>
              <ul className="mt-6 space-y-4">
                {principles.map((item) => (
                  <li key={item.title} className="flex gap-4 border-l-2 border-background-800 pl-4">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-900 text-accent-400">
                      <i className={`${item.icon} text-lg`} />
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-background-50">{item.title}</span>
                      <span className="mt-1 block text-xs leading-relaxed text-background-400">{item.desc}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="border-y border-background-200/70 bg-background-100/60" aria-labelledby="about-process-title">
          <div className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
            <p className="text-xs font-medium tracking-[0.25em] text-primary-600">PROCESS</p>
            <h2 id="about-process-title" className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              内容是怎么整理出来的
            </h2>
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {steps.map((item, index) => (
                <Reveal key={item.step} delay={index * 60}>
                  <div className="h-full rounded-lg border border-background-200/70 bg-background-50 p-5">
                    <span className="font-heading text-2xl font-bold text-accent-500">{item.step}</span>
                    <h3 className="mt-3 text-sm font-semibold text-foreground-950">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16" aria-labelledby="about-voice-title">
          <p className="text-xs font-medium tracking-[0.25em] text-primary-600">VOICE</p>
          <h2 id="about-voice-title" className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            用户怎么说
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((item, index) => (
              <Reveal key={item.name} delay={index * 60}>
                <figure className="flex h-full flex-col rounded-lg border border-background-200/70 bg-background-100/60 p-5 md:p-6">
                  <i className="ri-double-quotes-l text-2xl text-primary-300" />
                  <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                    {item.content}
                  </blockquote>
                  <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200/70 pt-4">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-200 text-sm font-medium text-secondary-900">
                      {item.name.slice(0, 1)}
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-950">{item.name}</span>
                      <span className="block text-xs text-foreground-500">{item.role}</span>
                    </span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-[1280px] px-4 pb-16 md:px-6" aria-labelledby="about-contact-title">
          <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:gap-12">
            <div>
              <p className="text-xs font-medium tracking-[0.25em] text-primary-600">CONTACT</p>
              <h2 id="about-contact-title" className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                联系方式
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                内容纠错、线索补充、词条收录申请与商务合作，都可以直接联系我们。邮件请注明涉及的人物或事件名称，
                便于我们快速定位词条。
              </p>
              <div className="mt-6 space-y-3">
                <a
                  href={`mailto:${siteInfo.email}`}
                  className="flex items-center gap-3 rounded-lg border border-background-200/70 bg-background-50 p-4 transition-colors hover:border-primary-300"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className="ri-mail-line text-lg" />
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">邮箱</span>
                    <span className="block text-sm text-foreground-950">{siteInfo.email}</span>
                  </span>
                </a>
                <div className="flex items-center gap-3 rounded-lg border border-background-200/70 bg-background-50 p-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className="ri-time-line text-lg" />
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">回复时效</span>
                    <span className="block text-sm text-foreground-950">三个工作日内</span>
                  </span>
                </div>
                <div className="flex items-center gap-3 rounded-lg border border-background-200/70 bg-background-50 p-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className="ri-shield-check-line text-lg" />
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">隐私说明</span>
                    <span className="block text-sm text-foreground-950">留言信息仅用于本次沟通</span>
                  </span>
                </div>
              </div>
            </div>
            <ContactForm />
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}